import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useSelector} from 'react-redux';
import {IFetchRoom, IUpdateRoom} from '../../../../incase-domain/rooms/room.editor';
import {useFetchRoom, useUpdateRoom} from '../queries';
import EditRoomIdentificationTab from './components/EditRoomIdentificationTab';
import Button from '../../../../incase-domain/components/button/Button';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import VerticalNavigation from '../../../../incase-domain/components/verticalNavigation/VerticalNavigation';
import EditRoomGeneralTab from './components/EditRoomGeneralTab';
import EditRoomPresenterTab from './components/EditRoomPresenterTab';
import EditRoomAccessTab from './components/EditRoomAccessTab';
import EditRoomGamificationTab from './components/EditRoomGamificationTab';
import {ANONYMOUS_PRESENTER_VALUE, EditRoomFormType, editRoomValidator} from '../../../../incase-domain/validators/room/room';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';

interface EditRoomModalProps {
    roomId: string | undefined;
    onCancel: () => void;
}

function EditRoomModal({roomId, onCancel}: EditRoomModalProps): JSX.Element | null {
    const {t} = useTranslation();
    const user = useSelector(loggedInUserSelector);

    const [activeTab, setActiveTab] = useState(0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const useFormReturn: UseFormReturn<any, unknown> = useForm<EditRoomFormType>({
        resolver: zodResolver(editRoomValidator),
    });

    const {handleSubmit, reset} = useFormReturn;

    const {data: room} = useFetchRoom({
        roomId: roomId || '',
        onSuccess: (room: IFetchRoom): void => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {presentation, presenter, ...filteredRoom} = room;

            if (filteredRoom.presenterEmail && !filteredRoom.presenterId) {
                filteredRoom.presenterId = ANONYMOUS_PRESENTER_VALUE;
            }
            if (!filteredRoom.presenterId && !filteredRoom.presenterEmail && user?._id) {
                filteredRoom.presenterId = user?._id;
            }

            reset(filteredRoom);
        },
    });

    const {mutate: updateRoom, isLoading} = useUpdateRoom(onCancel);

    const onSubmit: SubmitHandler<EditRoomFormType> = (data) => {
        if (isLoading) {
            return;
        }
        const transformedData: IUpdateRoom = {
            ...data,
            startTime: data.startTime || null,
            endTime: data.endTime || null,
        };
        if (transformedData.presenterId === (user?._id as string) || transformedData.presenterId !== ANONYMOUS_PRESENTER_VALUE) {
            transformedData.presenterEmail = '';
        }
        if (transformedData.presenterId === ANONYMOUS_PRESENTER_VALUE) {
            transformedData.presenterId = '';
        }
        updateRoom(transformedData);
    };

    const handleCancel = (): void => {
        onCancel();
        setActiveTab(0);
        reset();
    };

    if (!roomId) return null;

    return (
        <Modal
            isOpen={!!roomId}
            onCancel={handleCancel}
            header={room?.roomName}
            description="Configure room preferences"
            icon="solar:settings-bold"
            size="lg"
            noPadding
            dataCy="edit_room_modal"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="c-editRoomModal">
                <div className="c-editRoomModal__navigation">
                    <VerticalNavigation
                        items={[
                            {label: t('roomsPage.editRoomModal.general'), icon: 'solar:presentation-graph-linear'},
                            {label: t('roomsPage.editRoomModal.presenter'), icon: 'solar:microphone-2-linear'},
                            {label: t('roomsPage.editRoomModal.access'), icon: 'solar:lock-keyhole-minimalistic-linear'},
                            {label: t('roomsPage.editRoomModal.groups'), icon: 'solar:user-id-linear'},
                            {label: t('presEditPage.settingsModal.gamification'), icon: 'solar:gamepad-minimalistic-linear'},
                        ]}
                        onChange={(index) => setActiveTab(index)}
                        initialIndex={activeTab}
                    />
                </div>

                <div className="c-editRoomModal__content">
                    {activeTab === 0 && <EditRoomGeneralTab useForm={useFormReturn} />}

                    {activeTab === 1 && <EditRoomPresenterTab useForm={useFormReturn} />}

                    {activeTab === 2 && <EditRoomAccessTab room={room} useForm={useFormReturn} />}

                    {activeTab === 3 && <EditRoomIdentificationTab useForm={useFormReturn} />}

                    {activeTab === 4 && <EditRoomGamificationTab useForm={useFormReturn} />}

                    <div className="c-editRoomModal__content_footer">
                        <Button
                            variant="solid"
                            size="sm"
                            color="secondary"
                            wide
                            loading={isLoading}
                            disabled={isLoading}
                            dataCy="save_room"
                            type="submit"
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}

export default EditRoomModal;
